// frontend/src/pages/Portfolio.tsx
// Portfolio Management with City View Toggle
// Single upload flow - City view aggregates postal codes to cities
// Updated: Added navigation state handling for Dashboard integration

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Container,
  Grid,
  Typography,
  Card,
  CardContent,
  Button,
  Box,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Paper,
  LinearProgress,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Tabs,
  Tab,
  Tooltip,
  Snackbar,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
  Divider,
  Badge,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import LocationCityIcon from '@mui/icons-material/LocationCity';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import DeleteIcon from '@mui/icons-material/Delete';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import DownloadIcon from '@mui/icons-material/Download';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import RefreshIcon from '@mui/icons-material/Refresh';
import MapIcon from '@mui/icons-material/Map';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SaveIcon from '@mui/icons-material/Save';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import Papa from 'papaparse';
import * as XLSX from 'xlsx';
import MultiLevelPortfolioView from '../components/Portfolio/MultiLevelPortfolioView';
import GeographicConcentration from '../components/Portfolio/GeographicConcentration';
import ClimateRiskBreakdown from '../components/Portfolio/ClimateRiskBreakdown';
import EnvironmentalRiskBreakdown from '../components/Portfolio/EnvironmentalRiskBreakdown';
import PercentileRankings from '../components/Portfolio/PercentileRankings';
import TimeBasedProjections from '../components/Portfolio/TimeBasedProjections';
import ScenarioComparisonTool from '../components/Portfolio/ScenarioComparisonTool';
import PortfolioBenchmark from '../components/Portfolio/PortfolioBenchmark';
import TCFDReporting from '../components/Portfolio/TCFDReporting';
import RegulatoryComplianceDashboard from '../components/Portfolio/RegulatoryComplianceDashboard';
import MarketImpactDashboard from '../components/Portfolio/MarketImpactDashboard';
import MarketBacktesting from '../components/Portfolio/MarketBacktesting';
import WeatherAlerts from '../components/WeatherAlerts';
import { formatLargeNumber } from '../utils/portfolioHelpers';
import {
  savePortfolioAnalysis,
  getPortfolioAnalysis,
  deletePortfolioAnalysis,
  setCurrentAnalysis,
  getCurrentAnalysis,
  clearCurrentAnalysis,
  saveMapData,
  clearMapData
} from '../utils/portfolioCache';
import zipcodes from 'zipcodes';

const API_URL = process.env.REACT_APP_API_URL || 'https://spatial-risk-api.onrender.com/api';

type ViewMode = 'postal_code' | 'city';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ py: 2 }}>{children}</Box>}
    </div>
  );
}

interface PortfolioItem {
  id?: string;
  postal_code?: string;
  postalCode?: string;
  city?: string;
  state?: string;
  latitude?: number;
  longitude?: number;
  risk_score?: number;
  asset_value?: number | string;
  assetValue?: number | string;
  population?: number;
  climate_eal?: number;
  top_physical_risk?: string;
  [key: string]: any;
}

interface PortfolioData {
  id: string;
  name: string;
  items: PortfolioItem[];
  item_count?: number;
  total_value?: number;
  totalValue?: number;
  avg_risk?: number;
  avgRisk?: number;
  analyzed?: boolean;
  [key: string]: any;
}

interface ParsedItem {
  postalCode?: string;
  city?: string;
  state?: string;
  assetValue: number;
}

interface CityAggregation {
  city: string;
  state: string;
  postalCodes: string[];
  totalValue: number;
  avgRisk: number;
  population: number;
  itemCount: number;
  latitude?: number;
  longitude?: number;
  top_physical_risk?: string;
  items: PortfolioItem[];
}

// Safe number parsing
const safeNumber = (value: any): number => {
  if (typeof value === 'number' && !isNaN(value)) return value;
  if (typeof value === 'string') {
    const cleaned = value.replace(/[$,]/g, '');
    const parsed = parseFloat(cleaned);
    return isNaN(parsed) ? 0 : parsed;
  }
  return 0;
};

// State name to code mapping
const stateNameToCode: { [key: string]: string } = {
  'alabama': 'AL', 'alaska': 'AK', 'arizona': 'AZ', 'arkansas': 'AR', 'california': 'CA',
  'colorado': 'CO', 'connecticut': 'CT', 'delaware': 'DE', 'florida': 'FL', 'georgia': 'GA',
  'hawaii': 'HI', 'idaho': 'ID', 'illinois': 'IL', 'indiana': 'IN', 'iowa': 'IA',
  'kansas': 'KS', 'kentucky': 'KY', 'louisiana': 'LA', 'maine': 'ME', 'maryland': 'MD',
  'massachusetts': 'MA', 'michigan': 'MI', 'minnesota': 'MN', 'mississippi': 'MS', 'missouri': 'MO',
  'montana': 'MT', 'nebraska': 'NE', 'nevada': 'NV', 'new hampshire': 'NH', 'new jersey': 'NJ',
  'new mexico': 'NM', 'new york': 'NY', 'north carolina': 'NC', 'north dakota': 'ND', 'ohio': 'OH',
  'oklahoma': 'OK', 'oregon': 'OR', 'pennsylvania': 'PA', 'rhode island': 'RI', 'south carolina': 'SC',
  'south dakota': 'SD', 'tennessee': 'TN', 'texas': 'TX', 'utah': 'UT', 'vermont': 'VT',
  'virginia': 'VA', 'washington': 'WA', 'west virginia': 'WV', 'wisconsin': 'WI', 'wyoming': 'WY',
  'district of columbia': 'DC'
};

const getStateCode = (state: string): string => {
  if (state.length === 2) return state.toUpperCase();
  return stateNameToCode[state.toLowerCase()] || state.toUpperCase().slice(0, 2);
};

// Parse value - handles currency formatting, commas, etc.
function parseAssetValue(value: any): number {
  if (typeof value === 'number') return value;
  if (!value) return 0;
  const str = String(value).replace(/[$,\s]/g, '').trim();
  const parsed = parseFloat(str);
  return isNaN(parsed) ? 0 : parsed;
}

// Parse CSV using Papa Parse - handles quoted values with commas correctly
function parseCSV(text: string): ParsedItem[] {
  console.log('Parsing CSV with Papa Parse...');
  
  const result = Papa.parse(text, {
    header: true,
    skipEmptyLines: true,
    transformHeader: (header: string) => header.toLowerCase().trim(),
  });
  
  console.log('Papa Parse result:', result.meta.fields, 'rows:', result.data.length);
  
  if (result.errors.length > 0) {
    console.warn('CSV parse errors:', result.errors);
  }
  
  const items: ParsedItem[] = [];
  const fields = result.meta.fields || [];
  
  // Find column indices
  const postalField = fields.find(f => 
    f.includes('zip') || f.includes('postal') || f === 'code'
  );
  const cityField = fields.find(f => f.includes('city'));
  const stateField = fields.find(f => f.includes('state'));
  const valueField = fields.find(f => 
    f.includes('value') || f.includes('asset') || f.includes('amount') || f.includes('price')
  );
  
  console.log('Detected fields:', { postalField, cityField, stateField, valueField });
  
  for (const row of result.data as any[]) {
    // Try postal code first
    if (postalField && row[postalField]) {
      const rawPostal = String(row[postalField]).replace(/[^0-9]/g, '').padStart(5, '0');
      if (rawPostal.length === 5 && /^\d{5}$/.test(rawPostal)) {
        const zipData = zipcodes.lookup(rawPostal);
        const rawValue = valueField ? row[valueField] : 0;
        const assetValue = parseAssetValue(rawValue);
        console.log('Parsed row:', rawPostal, 'value:', rawValue, '-> parsed:', assetValue);
        items.push({
          postalCode: rawPostal,
          city: zipData?.city,
          state: zipData?.state,
          assetValue
        });
        continue;
      }
    }
    
    // Try city/state
    if (cityField && stateField && row[cityField] && row[stateField]) {
      const city = String(row[cityField]).trim();
      const state = getStateCode(String(row[stateField]).trim());
      const rawValue = valueField ? row[valueField] : 0;
      const assetValue = parseAssetValue(rawValue);
      if (city && state) {
        items.push({ city, state, assetValue });
        continue;
      }
    }
  }
  
  // If no header-based parsing worked, try headerless parsing
  if (items.length === 0 && result.data.length > 0) {
    console.log('No items from header parsing, trying headerless...');
    return parseCSVHeaderless(text);
  }
  
  console.log('Parsed', items.length, 'items from CSV');
  return items;
}

// Parse CSV without headers
function parseCSVHeaderless(text: string): ParsedItem[] {
  const result = Papa.parse(text, {
    header: false,
    skipEmptyLines: true,
  });
  
  const items: ParsedItem[] = [];
  
  for (const row of result.data as string[][]) {
    if (!row || row.length === 0) continue;
    
    const firstVal = String(row[0] || '').trim();
    const firstDigits = firstVal.replace(/[^0-9]/g, '');
    
    // Check if first column is postal code
    if (firstDigits.length === 5 && /^\d{5}$/.test(firstDigits)) {
      const postalCode = firstDigits;
      const assetValue = row[1] ? parseAssetValue(row[1]) : 0;
      const zipData = zipcodes.lookup(postalCode);
      items.push({
        postalCode,
        city: zipData?.city,
        state: zipData?.state,
        assetValue
      });
    }
    // City, State, Value format
    else if (row.length >= 2) {
      const city = firstVal;
      const state = getStateCode(String(row[1] || '').trim());
      const assetValue = row[2] ? parseAssetValue(row[2]) : 0;
      if (city && state) {
        items.push({ city, state, assetValue });
      }
    }
  }
  
  return items;
}

// Parse Excel file (.xlsx, .xls)
function parseExcel(data: ArrayBuffer): ParsedItem[] {
  console.log('Parsing Excel file...');
  
  const workbook = XLSX.read(data, { type: 'array' });
  const firstSheetName = workbook.SheetNames[0];
  const worksheet = workbook.Sheets[firstSheetName];
  
  // Convert to JSON with header row
  const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 }) as any[][];
  
  if (jsonData.length === 0) return [];
  
  const items: ParsedItem[] = [];
  
  // Check if first row is header
  const firstRow = jsonData[0].map((cell: any) => String(cell || '').toLowerCase().trim());
  const hasHeader = firstRow.some((cell: string) => 
    cell.includes('zip') || cell.includes('postal') || cell.includes('city') || 
    cell.includes('value') || cell.includes('asset')
  );
  
  console.log('Excel first row:', firstRow, 'hasHeader:', hasHeader);
  
  if (hasHeader) {
    // Find column indices
    const postalIdx = firstRow.findIndex((h: string) => 
      h.includes('zip') || h.includes('postal') || h === 'code'
    );
    const cityIdx = firstRow.findIndex((h: string) => h.includes('city'));
    const stateIdx = firstRow.findIndex((h: string) => h.includes('state'));
    const valueIdx = firstRow.findIndex((h: string) => 
      h.includes('value') || h.includes('asset') || h.includes('amount') || h.includes('price')
    );
    
    console.log('Excel column indices:', { postalIdx, cityIdx, stateIdx, valueIdx });
    
    for (let i = 1; i < jsonData.length; i++) {
      const row = jsonData[i];
      if (!row || row.length === 0) continue;
      
      // Try postal code
      if (postalIdx >= 0 && row[postalIdx]) {
        const rawPostal = String(row[postalIdx]).replace(/[^0-9]/g, '').padStart(5, '0');
        if (rawPostal.length === 5 && /^\d{5}$/.test(rawPostal)) {
          const zipData = zipcodes.lookup(rawPostal);
          const rawValue = valueIdx >= 0 ? row[valueIdx] : 0;
          const assetValue = parseAssetValue(rawValue);
          console.log('Excel row:', rawPostal, 'value:', rawValue, '-> parsed:', assetValue);
          items.push({
            postalCode: rawPostal,
            city: zipData?.city,
            state: zipData?.state,
            assetValue
          });
          continue;
        }
      }
      
      // Try city/state
      if (cityIdx >= 0 && stateIdx >= 0 && row[cityIdx] && row[stateIdx]) {
        const city = String(row[cityIdx]).trim();
        const state = getStateCode(String(row[stateIdx]).trim());
        const rawValue = valueIdx >= 0 ? row[valueIdx] : 0;
        const assetValue = parseAssetValue(rawValue);
        if (city && state) {
          items.push({ city, state, assetValue });
        }
      }
    }
  } else {
    // No header - guess format
    for (const row of jsonData) {
      if (!row || row.length === 0) continue;
      
      const firstVal = String(row[0] || '').trim();
      const firstDigits = firstVal.replace(/[^0-9]/g, '');
      
      if (firstDigits.length === 5 && /^\d{5}$/.test(firstDigits)) {
        const postalCode = firstDigits;
        const assetValue = row[1] ? parseAssetValue(row[1]) : 0;
        const zipData = zipcodes.lookup(postalCode);
        items.push({
          postalCode,
          city: zipData?.city,
          state: zipData?.state,
          assetValue
        });
      } else if (row.length >= 2) {
        const city = firstVal;
        const state = getStateCode(String(row[1] || '').trim());
        const assetValue = row[2] ? parseAssetValue(row[2]) : 0;
        if (city && state) {
          items.push({ city, state, assetValue });
        }
      }
    }
  }
  
  console.log('Parsed', items.length, 'items from Excel');
  return items;
}

// Aggregate postal codes to cities
function aggregateToCities(items: PortfolioItem[]): CityAggregation[] {
  const cityMap = new Map<string, CityAggregation>();
  
  for (const item of items) {
    let city = item.city;
    let state = item.state;
    
    // If no city/state, try to get from postal code
    if (!city || !state) {
      const postalCode = item.postal_code || item.postalCode;
      if (postalCode) {
        const zipData = zipcodes.lookup(postalCode);
        if (zipData) {
          city = zipData.city;
          state = zipData.state;
        }
      }
    }
    
    if (!city || !state) continue;
    
    const key = `${city.toLowerCase()}-${state.toUpperCase()}`;
    
    if (!cityMap.has(key)) {
      cityMap.set(key, {
        city,
        state: state.toUpperCase(),
        postalCodes: [],
        totalValue: 0,
        avgRisk: 0,
        population: 0,
        itemCount: 0,
        latitude: item.latitude,
        longitude: item.longitude,
        top_physical_risk: item.top_physical_risk,
        items: []
      });
    }
    
    const cityData = cityMap.get(key)!;
    const postalCode = item.postal_code || item.postalCode;
    if (postalCode && !cityData.postalCodes.includes(postalCode)) {
      cityData.postalCodes.push(postalCode);
    }
    cityData.totalValue += safeNumber(item.asset_value || item.assetValue);
    cityData.avgRisk += safeNumber(item.risk_score);
    cityData.population += safeNumber(item.population);
    cityData.itemCount++;
    cityData.items.push(item);
    
    // Use first valid coordinates
    if (!cityData.latitude && item.latitude) {
      cityData.latitude = item.latitude;
      cityData.longitude = item.longitude;
    }
  }
  
  // Calculate averages
  return Array.from(cityMap.values()).map(city => ({
    ...city,
    avgRisk: city.itemCount > 0 ? city.avgRisk / city.itemCount : 0
  }));
}

// View item type for consistent data across views
interface ViewItem {
  city?: string;
  state?: string;
  postal_code?: string;
  asset_value: number;
  risk_score: number;
  population?: number;
  latitude?: number;
  longitude?: number;
  top_physical_risk?: string;
  [key: string]: any;
}

interface ViewData {
  items: ViewItem[];
  totalValue: number;
  avgRisk: number;
  count: number;
}

// Navigation state interface
interface NavigationState {
  openTab?: string;
  selectedPortfolioId?: string;
}

export default function Portfolio() {
  const navigate = useNavigate();
  const location = useLocation();
  const [portfolios, setPortfolios] = useState<PortfolioData[]>([]);
  const [selectedPortfolio, setSelectedPortfolio] = useState<PortfolioData | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>('postal_code');
  const [cityAggregation, setCityAggregation] = useState<CityAggregation[]>([]);
  const [loading, setLoading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  
  // Dialog state
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogTab, setDialogTab] = useState(0);
  const [portfolioName, setPortfolioName] = useState('');
  const [portfolioItems, setPortfolioItems] = useState<ParsedItem[]>([]);
  const [manualInput, setManualInput] = useState('');
  
  // Analysis tabs
  const [analysisTabValue, setAnalysisTabValue] = useState(0);
  
  // Menu state
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [menuPortfolio, setMenuPortfolio] = useState<PortfolioData | null>(null);
  
  // View/Edit dialog state
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [viewEditPortfolio, setViewEditPortfolio] = useState<PortfolioData | null>(null);
  const [editedItems, setEditedItems] = useState<any[]>([]);
  const [editedName, setEditedName] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);
  
  const token = localStorage.getItem('token');
  
  // Get userId from logged-in user - maintains privacy per user
  const userId = useMemo(() => {
    try {
      const userStr = localStorage.getItem('user');
      if (userStr) {
        const user = JSON.parse(userStr);
        console.log('Portfolio: Using userId from logged-in user:', user.id);
        return user.id || 'demo';
      }
    } catch (e) {
      console.error('Error parsing user from localStorage:', e);
    }
    return 'demo'; // Fallback for non-logged-in users
  }, []);

  // Fetch portfolios for current user only
  const fetchPortfolios = useCallback(async () => {
    setLoading(true);
    console.log('Portfolio page: Fetching portfolios for userId:', userId);
    try {
      const response = await axios.get(`${API_URL}/portfolios`, {
        params: { userId },
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log('Fetched portfolios:', response.data.length, 'for user:', userId);
      setPortfolios(response.data);
    } catch (err) {
      console.error('Error fetching portfolios:', err);
      setError('Failed to load portfolios');
    } finally {
      setLoading(false);
    }
  }, [token, userId]);

  useEffect(() => {
    fetchPortfolios();
    
    // Restore analysis state from IndexedDB
    const restoreAnalysis = async () => {
      try {
        const savedData = await getCurrentAnalysis();
        if (savedData) {
          console.log('Restored analysis from IndexedDB:', savedData.name, savedData.items?.length, 'items');
          setSelectedPortfolio(savedData);
        }
      } catch (e) {
        console.error('Failed to restore analysis from IndexedDB:', e);
      }
    };
    
    restoreAnalysis();
  }, [fetchPortfolios]);

  // Handle navigation state from Dashboard
  useEffect(() => {
    const navState = location.state as NavigationState | null;
    
    if (navState?.openTab === 'weather-alerts' && selectedPortfolio) {
      // Weather Alerts is tab index 8
      setAnalysisTabValue(8);
      // Clear the state so it doesn't persist on refresh
      window.history.replaceState({}, document.title);
    }
    
    if (navState?.selectedPortfolioId && portfolios.length > 0 && !selectedPortfolio) {
      // Find and select the portfolio
      const portfolio = portfolios.find(p => p.id === navState.selectedPortfolioId);
      if (portfolio) {
        handleAnalyze(portfolio);
      }
      // Clear the state
      window.history.replaceState({}, document.title);
    }
  }, [location.state, portfolios, selectedPortfolio]);

  // When selected portfolio changes, recalculate city aggregation
  useEffect(() => {
    if (selectedPortfolio?.items) {
      const cities = aggregateToCities(selectedPortfolio.items);
      setCityAggregation(cities);
    }
  }, [selectedPortfolio]);

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const fileName = file.name.toLowerCase();
    const isExcel = fileName.endsWith('.xlsx') || fileName.endsWith('.xls');
    
    console.log('Uploading file:', fileName, 'isExcel:', isExcel);

    if (isExcel) {
      // Read Excel file as ArrayBuffer
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = e.target?.result as ArrayBuffer;
          const items = parseExcel(data);
          setPortfolioItems(items);
          const totalValue = items.reduce((sum, item) => sum + item.assetValue, 0);
          setSuccessMessage(`Loaded ${items.length} items from Excel (Total: $${totalValue.toLocaleString()})`);
        } catch (error) {
          console.error('Excel parse error:', error);
          setError('Failed to parse Excel file. Please check the format.');
        }
      };
      reader.readAsArrayBuffer(file);
    } else {
      // Read CSV/TXT file as text
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const text = e.target?.result as string;
          const items = parseCSV(text);
          setPortfolioItems(items);
          const totalValue = items.reduce((sum, item) => sum + item.assetValue, 0);
          setSuccessMessage(`Loaded ${items.length} items from CSV (Total: $${totalValue.toLocaleString()})`);
        } catch (error) {
          console.error('CSV parse error:', error);
          setError('Failed to parse CSV file. Please check the format.');
        }
      };
      reader.readAsText(file);
    }
    
    // Reset input so same file can be uploaded again
    event.target.value = '';
  };

  const handleManualInputChange = (value: string) => {
    setManualInput(value);
    
    // Use Papa Parse for manual input too - handles quoted values correctly
    const result = Papa.parse(value, {
      header: false,
      skipEmptyLines: true,
    });
    
    const items: ParsedItem[] = [];
    
    for (const row of result.data as string[][]) {
      if (!row || row.length === 0) continue;
      
      const firstPart = String(row[0] || '').trim();
      const firstDigits = firstPart.replace(/[^0-9]/g, '');
      
      // Postal code format
      if (firstDigits.length === 5 && /^\d{5}$/.test(firstDigits)) {
        const postalCode = firstDigits;
        const assetValue = row[1] ? parseAssetValue(row[1]) : 0;
        const zipData = zipcodes.lookup(postalCode);
        items.push({ 
          postalCode, 
          city: zipData?.city,
          state: zipData?.state,
          assetValue 
        });
      }
      // City, State, Value format
      else if (row.length >= 2) {
        const city = firstPart;
        const state = getStateCode(String(row[1] || '').trim());
        const assetValue = row[2] ? parseAssetValue(row[2]) : 0;
        if (city && state) {
          items.push({ city, state, assetValue });
        }
      }
    }
    
    setPortfolioItems(items);
  };

  // Helper function to notify Dashboard of portfolio changes
  const notifyDashboardUpdate = () => {
    window.dispatchEvent(new Event('portfolioUpdated'));
    localStorage.setItem('portfolioUpdated', Date.now().toString());
  };

  const createPortfolio = async () => {
    if (!portfolioName.trim()) {
      setError('Please provide a portfolio name');
      return;
    }
    if (portfolioItems.length === 0) {
      setError('Please add at least one location');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Convert to API format
      const items = portfolioItems.map(item => ({
        postalCode: item.postalCode || '',
        assetValue: item.assetValue,
        city: item.city,
        state: item.state
      }));

      console.log('Creating portfolio with items:', items.slice(0, 3));
      console.log('Total items:', items.length);
      console.log('Total value:', items.reduce((sum, i) => sum + i.assetValue, 0));

      const response = await axios.post(
        `${API_URL}/portfolios`,
        { name: portfolioName, items, userId }, // Use logged-in user's ID
        { headers: { Authorization: `Bearer ${token}` } }
      );

      console.log('Portfolio created, response:', response.data);

      setSuccessMessage(`Created "${portfolioName}" with ${response.data.items?.length || portfolioItems.length} items`);
      
      // Notify Dashboard of the update
      notifyDashboardUpdate();
      
      setOpenDialog(false);
      setPortfolioName('');
      setPortfolioItems([]);
      setManualInput('');
      fetchPortfolios();
    } catch (err: any) {
      console.error('Create error:', err);
      setError(err.response?.data?.error || 'Failed to create portfolio');
    } finally {
      setLoading(false);
    }
  };

  const handleAnalyze = async (portfolio: PortfolioData, forceRefresh = false) => {
    setAnalyzing(true);
    setError('');
    
    // Clear old cache if forcing refresh
    if (forceRefresh) {
      await deletePortfolioAnalysis(portfolio.id);
    }
    
    try {
      const response = await axios.get(
        `${API_URL}/portfolios/${portfolio.id}/analysis`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Log what the backend actually returns
      console.log('Backend response - total_value:', response.data.total_value);
      console.log('Backend response - items sample:', response.data.items?.slice(0, 3).map((i: any) => ({
        postal_code: i.postal_code,
        asset_value: i.asset_value,
        assetValue: i.assetValue,
        risk_score: i.risk_score,
      })));
      
      const analyzedPortfolio = {
        ...response.data,
        analyzed: true,
        analyzedAt: new Date().toISOString()
      };

      setPortfolios(prev =>
        prev.map(p => p.id === portfolio.id ? { ...p, analyzed: true } : p)
      );

      setSelectedPortfolio(analyzedPortfolio);
      setViewMode('postal_code'); // Reset to postal code view
      
      // Cache in IndexedDB (handles large portfolios without quota issues)
      try {
        await savePortfolioAnalysis(portfolio.id, analyzedPortfolio);
        await setCurrentAnalysis(analyzedPortfolio);
        console.log(`Cached portfolio ${portfolio.id} with ${analyzedPortfolio.items?.length || 0} items`);
      } catch (cacheError) {
        console.warn('Failed to cache portfolio in IndexedDB:', cacheError);
        // Continue without caching - data is still in React state
      }
      
      setSuccessMessage('Analysis complete!');
    } catch (err: any) {
      console.error('Analysis error:', err);
      setError(err.response?.data?.error || 'Failed to analyze portfolio');
    } finally {
      setAnalyzing(false);
    }
  };

  const handleDismissAnalysis = async () => {
    setSelectedPortfolio(null);
    await clearCurrentAnalysis();
    setSuccessMessage('Analysis dismissed');
  };

  const handleRefreshAnalysis = async () => {
    if (!selectedPortfolio) return;
    await deletePortfolioAnalysis(selectedPortfolio.id);
    await handleAnalyze(selectedPortfolio, true);
  };

  const handleDelete = async (portfolioId: string) => {
    if (!window.confirm('Are you sure you want to delete this portfolio?')) return;

    try {
      await axios.delete(`${API_URL}/portfolios/${portfolioId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSuccessMessage('Portfolio deleted');
      
      // Notify Dashboard of the update
      notifyDashboardUpdate();
      
      fetchPortfolios();
      
      if (selectedPortfolio?.id === portfolioId) {
        handleDismissAnalysis();
      }
    } catch (err: any) {
      console.error('Delete error:', err);
      setError('Failed to delete portfolio');
    }
  };

  const handleMap = async (portfolio: PortfolioData) => {
    try {
      const response = await axios.get(
        `${API_URL}/portfolios/${portfolio.id}/analysis`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const locations = response.data.items.map((item: PortfolioItem) => {
        const zipData = item.postal_code ? zipcodes.lookup(item.postal_code) : null;
        return {
          postal_code: item.postal_code,
          city: item.city || zipData?.city,
          state: item.state || zipData?.state,
          latitude: item.latitude || zipData?.latitude,
          longitude: item.longitude || zipData?.longitude,
          risk_score: safeNumber(item.risk_score),
          asset_value: safeNumber(item.asset_value),
          population: safeNumber(item.population),
          top_physical_risk: item.top_physical_risk,
          // Include all hazard risk fields
          hurricane: safeNumber(item.hurricane),
          flood: safeNumber(item.flood),
          flood_r: safeNumber(item.flood_r),
          wildfire: safeNumber(item.wildfire),
          earthquake: safeNumber(item.earthquake),
          drought: safeNumber(item.drought),
          heat: safeNumber(item.heat),
          cold: safeNumber(item.cold),
          tornado: safeNumber(item.tornado),
          wind: safeNumber(item.wind),
          hail: safeNumber(item.hail),
          ice: safeNumber(item.ice),
          winter: safeNumber(item.winter),
          lightning: safeNumber(item.lightning),
          landslide: safeNumber(item.landslide),
          tsunami: safeNumber(item.tsunami),
          volcanic: safeNumber(item.volcanic),
          avalanche: safeNumber(item.avalanche),
          // Environmental and community factors
          climate_eal: safeNumber(item.climate_eal),
          social_vulnerability: safeNumber(item.social_vulnerability),
          community_resilience: safeNumber(item.community_resilience),
        };
      });

      // Store for Map page - use viewMode to determine if city aggregation needed
      const mapData = {
        portfolioId: portfolio.id,
        portfolioName: portfolio.name,
        locations,
        geographicLevel: viewMode // 'postal_code' or 'city'
      };
      
      // Save to IndexedDB (handles large portfolios)
      try {
        await saveMapData(mapData);
        console.log(`Saved map data for ${locations.length} locations`);
      } catch (mapCacheError) {
        console.warn('Failed to cache map data:', mapCacheError);
        // Continue anyway - Map page can fetch from API if needed
      }
      
      navigate('/map');
    } catch (err) {
      console.error('Map error:', err);
      setError('Failed to load map data');
    }
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, portfolio: PortfolioData) => {
    setAnchorEl(event.currentTarget);
    setMenuPortfolio(portfolio);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuPortfolio(null);
  };

  // Handle View Portfolio (read-only)
  const handleViewPortfolio = (portfolio: PortfolioData) => {
    setViewEditPortfolio(portfolio);
    setViewDialogOpen(true);
  };

  // Handle Edit Portfolio
  const handleEditPortfolio = (portfolio: PortfolioData) => {
    setViewEditPortfolio(portfolio);
    setEditedName(portfolio.name);
    setEditedItems(portfolio.items?.map(item => ({
      ...item,
      postal_code: item.postal_code || item.zip_code || '',
      asset_value: item.asset_value || item.assetValue || 0,
    })) || []);
    setEditDialogOpen(true);
  };

  // Handle saving edited portfolio
  const handleSaveEdit = async () => {
    if (!viewEditPortfolio) return;
    
    setSavingEdit(true);
    try {
      // Format items for the API
      const formattedItems = editedItems.map(item => ({
        postalCode: item.postal_code,
        assetValue: parseFloat(String(item.asset_value).replace(/[$,]/g, '')) || 0,
      }));

      const response = await axios.put(
        `${API_URL}/portfolios/${viewEditPortfolio.id}`,
        { 
          name: editedName, 
          items: formattedItems,
          userId 
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setSuccessMessage(`Portfolio "${editedName}" updated successfully`);
      setEditDialogOpen(false);
      setViewEditPortfolio(null);
      
      // Refresh portfolios list
      fetchPortfolios();
      
      // If this portfolio was selected, refresh analysis
      if (selectedPortfolio?.id === viewEditPortfolio.id) {
        handleAnalyze(response.data, true);
      }
    } catch (err: any) {
      console.error('Error updating portfolio:', err);
      setError(err.response?.data?.error || 'Failed to update portfolio');
    } finally {
      setSavingEdit(false);
    }
  };

  // Handle updating an edited item
  const handleEditItemChange = (index: number, field: string, value: any) => {
    setEditedItems(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  // Handle removing an item from edit
  const handleRemoveEditItem = (index: number) => {
    setEditedItems(prev => prev.filter((_, i) => i !== index));
  };

  // Handle adding a new item in edit
  const handleAddEditItem = () => {
    setEditedItems(prev => [...prev, { postal_code: '', asset_value: 0, city: '', state: '' }]);
  };

  const handleViewModeChange = (event: React.MouseEvent<HTMLElement>, newMode: ViewMode | null) => {
    if (newMode !== null) {
      setViewMode(newMode);
    }
  };

  // Get current view data based on mode
  const getCurrentViewData = (): ViewData => {
    if (!selectedPortfolio) return { items: [], totalValue: 0, avgRisk: 0, count: 0 };
    
    if (viewMode === 'city') {
      const totalValue = cityAggregation.reduce((sum, c) => sum + c.totalValue, 0);
      const avgRisk = cityAggregation.length > 0
        ? cityAggregation.reduce((sum, c) => sum + c.avgRisk, 0) / cityAggregation.length
        : 0;
      return {
        items: cityAggregation.map(c => ({
          city: c.city,
          state: c.state,
          asset_value: c.totalValue,
          risk_score: c.avgRisk,
          population: c.population,
          postal_codes: c.postalCodes,
          item_count: c.itemCount,
          latitude: c.latitude,
          longitude: c.longitude,
          top_physical_risk: c.top_physical_risk
        })),
        totalValue,
        avgRisk,
        count: cityAggregation.length
      };
    }
    
    // Convert portfolio items to ViewItem format
    const items: ViewItem[] = (selectedPortfolio.items || []).map(item => ({
      ...item,
      asset_value: safeNumber(item.asset_value || item.assetValue),
      risk_score: safeNumber(item.risk_score)
    }));

    return {
      items,
      totalValue: safeNumber(selectedPortfolio.total_value || selectedPortfolio.totalValue),
      avgRisk: safeNumber(selectedPortfolio.avg_risk || selectedPortfolio.avgRisk),
      count: items.length
    };
  };

  const formatCurrency = (value: number): string => {
    if (!value) return '$0';
    if (value >= 1e9) return `$${(value / 1e9).toFixed(1)}B`;
    if (value >= 1e6) return `$${(value / 1e6).toFixed(1)}M`;
    if (value >= 1e3) return `$${(value / 1e3).toFixed(0)}K`;
    return `$${value.toFixed(0)}`;
  };

  // Get items for analysis tabs - ALWAYS use postal code level data
  // This ensures tabs have access to all hazard fields regardless of viewMode
  // Memoized to prevent excessive re-renders
  const analysisItems = useMemo(() => {
    if (!selectedPortfolio?.items) return [];
    
    // Debug logging (only once when items change)
    console.log('📊 Analysis items updated:', selectedPortfolio.items.length, 'items');
    
    return selectedPortfolio.items.map((item: any) => ({
      ...item,
      asset_value: safeNumber(item.asset_value || item.assetValue),
      risk_score: safeNumber(item.risk_score)
    }));
  }, [selectedPortfolio?.items]);

  // Wrapper function for backward compatibility
  const getAnalysisItems = (): any[] => analysisItems;

  const handleOpenDialog = () => {
    setOpenDialog(true);
    setDialogTab(0);
    setPortfolioName('');
    setPortfolioItems([]);
    setManualInput('');
    setError('');
  };

  // Delete ALL portfolios
  const handleDeleteAll = async () => {
    if (portfolios.length === 0) {
      setError('No portfolios to delete');
      return;
    }
    
    const confirmMessage = `Are you sure you want to delete ALL ${portfolios.length} portfolios? This action cannot be undone.`;
    if (!window.confirm(confirmMessage)) return;
    
    // Double confirmation for safety
    const doubleConfirm = window.prompt(`Type "DELETE ALL" to confirm deletion of ${portfolios.length} portfolios:`);
    if (doubleConfirm !== 'DELETE ALL') {
      setSuccessMessage('Deletion cancelled');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      let deletedCount = 0;
      let failedCount = 0;
      
      for (const portfolio of portfolios) {
        try {
          await axios.delete(`${API_URL}/portfolios/${portfolio.id}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          deletedCount++;
          console.log(`Deleted portfolio: ${portfolio.name} (${portfolio.id})`);
        } catch (err) {
          console.error(`Failed to delete portfolio ${portfolio.id}:`, err);
          failedCount++;
        }
      }
      
      // Clear selected portfolio and cache
      setSelectedPortfolio(null);
      await clearCurrentAnalysis();
      await clearMapData();
      
      // Notify Dashboard of the update
      notifyDashboardUpdate();
      
      // Refresh the portfolio list
      await fetchPortfolios();
      
      if (failedCount === 0) {
        setSuccessMessage(`Successfully deleted all ${deletedCount} portfolios`);
      } else {
        setSuccessMessage(`Deleted ${deletedCount} portfolios, ${failedCount} failed`);
      }
    } catch (err: any) {
      console.error('Delete all error:', err);
      setError('Failed to delete all portfolios');
    } finally {
      setLoading(false);
    }
  };

  const totalPortfolioValue = portfolioItems.reduce((sum, item) => sum + item.assetValue, 0);

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#111827' }}>
            Portfolio Manager
          </Typography>
          <Typography variant="body2" sx={{ color: '#6B7280', mt: 0.5 }}>
            Manage and analyze your climate risk portfolios
          </Typography>
        </Box>
        
        <Box sx={{ display: 'flex', gap: 2 }}>
          {portfolios.length > 0 && (
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={handleDeleteAll}
              disabled={loading}
            >
              Delete All ({portfolios.length})
            </Button>
          )}
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenDialog}
            sx={{ bgcolor: '#00D4FF' }}
          >
            New Portfolio
          </Button>
        </Box>
      </Box>

      {/* Error/Success Messages */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      
      <Snackbar
        open={!!successMessage}
        autoHideDuration={4000}
        onClose={() => setSuccessMessage('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity="success" onClose={() => setSuccessMessage('')}>
          {successMessage}
        </Alert>
      </Snackbar>

      {loading && <LinearProgress sx={{ mb: 3 }} />}

      {/* Portfolio Cards */}
      <Grid container spacing={3}>
        {portfolios.length === 0 && !loading && (
          <Grid item xs={12}>
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
                No portfolios yet
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Create your first portfolio to start analyzing climate risk
              </Typography>
              <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenDialog}>
                Create Portfolio
              </Button>
            </Paper>
          </Grid>
        )}

        {portfolios.map((portfolio) => (
          <Grid item xs={12} sm={6} md={4} key={portfolio.id}>
            <Card 
              sx={{ 
                height: '100%',
                cursor: 'pointer',
                transition: 'all 0.2s',
                border: selectedPortfolio?.id === portfolio.id ? '2px solid #00D4FF' : '1px solid #E5E7EB',
                '&:hover': { boxShadow: 4, transform: 'translateY(-2px)' }
              }}
              onClick={() => handleAnalyze(portfolio)}
            >
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {portfolio.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {portfolio.item_count || portfolio.items?.length || 0} locations
                    </Typography>
                  </Box>
                  <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleMenuOpen(e, portfolio); }}>
                    <MoreVertIcon />
                  </IconButton>
                </Box>

                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                  <Chip 
                    icon={<AttachMoneyIcon />} 
                    label={formatCurrency(safeNumber(portfolio.total_value || portfolio.totalValue))}
                    size="small"
                    sx={{ bgcolor: '#E8F5E9', color: '#2E7D32' }}
                  />
                  {portfolio.analyzed && (
                    <Chip label="Analyzed" size="small" color="primary" />
                  )}
                </Box>

                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<AnalyticsIcon />}
                    onClick={(e) => { e.stopPropagation(); handleAnalyze(portfolio); }}
                  >
                    Analyze
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<MapIcon />}
                    onClick={(e) => { e.stopPropagation(); handleMap(portfolio); }}
                  >
                    Map
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Analysis Panel */}
      {selectedPortfolio && (
        <Paper sx={{ mt: 4, p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 600 }}>
                {selectedPortfolio.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {viewMode === 'city' 
                  ? `${cityAggregation.length} cities (aggregated from ${selectedPortfolio.items?.length || 0} locations)`
                  : `${selectedPortfolio.items?.length || 0} locations`
                }
              </Typography>
            </Box>
            
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              {/* View Mode Toggle */}
              <ToggleButtonGroup
                value={viewMode}
                exclusive
                onChange={handleViewModeChange}
                size="small"
              >
                <ToggleButton value="postal_code" sx={{ px: 2 }}>
                  <LocationOnIcon sx={{ mr: 1, fontSize: 18 }} />
                  Locations
                </ToggleButton>
                <ToggleButton value="city" sx={{ px: 2 }}>
                  <LocationCityIcon sx={{ mr: 1, fontSize: 18 }} />
                  City View
                </ToggleButton>
              </ToggleButtonGroup>
              
              <Tooltip title="Refresh analysis">
                <IconButton onClick={handleRefreshAnalysis} disabled={analyzing}>
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="View on map">
                <IconButton onClick={() => handleMap(selectedPortfolio)}>
                  <MapIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Close analysis">
                <IconButton onClick={handleDismissAnalysis}>
                  <CloseIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          {analyzing && <LinearProgress sx={{ mb: 2 }} />}

          {/* Summary Cards */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={6} md={3}>
              <Paper sx={{ p: 2, bgcolor: '#F0FDF4', textAlign: 'center' }}>
                <Typography variant="h5" sx={{ fontWeight: 700, color: '#166534' }}>
                  {formatCurrency(getCurrentViewData().totalValue)}
                </Typography>
                <Typography variant="body2" color="text.secondary">Total Value</Typography>
              </Paper>
            </Grid>
            <Grid item xs={6} md={3}>
              <Paper sx={{ p: 2, bgcolor: '#FEF3C7', textAlign: 'center' }}>
                <Typography variant="h5" sx={{ fontWeight: 700, color: '#92400E' }}>
                  {getCurrentViewData().avgRisk.toFixed(1)}
                </Typography>
                <Typography variant="body2" color="text.secondary">Avg Risk Score</Typography>
              </Paper>
            </Grid>
            <Grid item xs={6} md={3}>
              <Paper sx={{ p: 2, bgcolor: '#EFF6FF', textAlign: 'center' }}>
                <Typography variant="h5" sx={{ fontWeight: 700, color: '#1E40AF' }}>
                  {getCurrentViewData().count}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {viewMode === 'city' ? 'Cities' : 'Locations'}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={6} md={3}>
              <Paper sx={{ p: 2, bgcolor: '#FDF2F8', textAlign: 'center' }}>
                <Typography variant="h5" sx={{ fontWeight: 700, color: '#BE185D' }}>
                  {getCurrentViewData().items.filter(i => i.risk_score > 6).length}
                </Typography>
                <Typography variant="body2" color="text.secondary">High Risk</Typography>
              </Paper>
            </Grid>
          </Grid>

          {/* Analysis Tabs */}
          <Tabs 
            value={analysisTabValue} 
            onChange={(e, v) => setAnalysisTabValue(v)}
            sx={{ borderBottom: 1, borderColor: 'divider' }}
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab label="Climate Risk" />
            <Tab label="Environmental" />
            <Tab label="Percentile Rankings" />
            <Tab label="Projections" />
            <Tab label="Scenarios" />
            <Tab label="Benchmark" />
            <Tab label="Compliance" />
            <Tab label="TCFD Report" />
            <Tab label="Market Impact" />
            <Tab label="Backtesting" />
            <Tab label="Weather Alerts" />
          </Tabs>

          <TabPanel value={analysisTabValue} index={0}>
            <ClimateRiskBreakdown portfolio={{
              ...selectedPortfolio,
              items: getAnalysisItems(),
              viewMode
            }} />
          </TabPanel>

          <TabPanel value={analysisTabValue} index={1}>
            <EnvironmentalRiskBreakdown portfolio={{
              ...selectedPortfolio,
              items: getAnalysisItems()
            }} />
          </TabPanel>

          <TabPanel value={analysisTabValue} index={2}>
            <PercentileRankings portfolio={{
              ...selectedPortfolio,
              items: getAnalysisItems()
            }} />
          </TabPanel>

          <TabPanel value={analysisTabValue} index={3}>
            <TimeBasedProjections portfolio={{
              ...selectedPortfolio,
              items: getAnalysisItems()
            }} />
          </TabPanel>

          <TabPanel value={analysisTabValue} index={4}>
            <ScenarioComparisonTool portfolio={{
              ...selectedPortfolio,
              items: getAnalysisItems()
            }} />
          </TabPanel>

          <TabPanel value={analysisTabValue} index={5}>
            <PortfolioBenchmark portfolio={{
              ...selectedPortfolio,
              items: getAnalysisItems()
            }} />
          </TabPanel>

          <TabPanel value={analysisTabValue} index={6}>
            <RegulatoryComplianceDashboard portfolio={{
              ...selectedPortfolio,
              items: getAnalysisItems()
            }} />
          </TabPanel>

          <TabPanel value={analysisTabValue} index={7}>
            <TCFDReporting portfolio={{
              ...selectedPortfolio,
              items: getAnalysisItems()
            }} />
          </TabPanel>

          <TabPanel value={analysisTabValue} index={8}>
            <MarketImpactDashboard portfolio={{
              ...selectedPortfolio,
              items: getAnalysisItems()
            }} />
          </TabPanel>

          <TabPanel value={analysisTabValue} index={9}>
            <MarketBacktesting portfolio={{
              ...selectedPortfolio,
              items: getAnalysisItems()
            }} />
          </TabPanel>

          <TabPanel value={analysisTabValue} index={10}>
            <WeatherAlerts 
              locations={getAnalysisItems().map((item: any) => ({
                postalCode: item.postal_code,
                city: item.city,
                state: item.state,
                latitude: item.latitude,
                longitude: item.longitude,
                risk_score: safeNumber(item.risk_score),
                asset_value: safeNumber(item.asset_value)
              }))}
              portfolioId={selectedPortfolio.id}
            />
          </TabPanel>
        </Paper>
      )}

      {/* Portfolio Menu */}
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        <MenuItem onClick={() => { handleMenuClose(); if (menuPortfolio) handleViewPortfolio(menuPortfolio); }}>
          <VisibilityIcon sx={{ mr: 1 }} /> View
        </MenuItem>
        <MenuItem onClick={() => { handleMenuClose(); if (menuPortfolio) handleEditPortfolio(menuPortfolio); }}>
          <EditIcon sx={{ mr: 1 }} /> Edit
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => { handleMenuClose(); if (menuPortfolio) handleAnalyze(menuPortfolio); }}>
          <AnalyticsIcon sx={{ mr: 1 }} /> Analyze
        </MenuItem>
        <MenuItem onClick={() => { handleMenuClose(); if (menuPortfolio) handleMap(menuPortfolio); }}>
          <MapIcon sx={{ mr: 1 }} /> View Map
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => { handleMenuClose(); if (menuPortfolio) handleDelete(menuPortfolio.id); }} sx={{ color: 'error.main' }}>
          <DeleteIcon sx={{ mr: 1 }} /> Delete
        </MenuItem>
      </Menu>

      {/* Create Portfolio Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Create New Portfolio</Typography>
            <IconButton onClick={() => setOpenDialog(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        
        <DialogContent>
          <TextField
            fullWidth
            label="Portfolio Name"
            value={portfolioName}
            onChange={(e) => setPortfolioName(e.target.value)}
            sx={{ mb: 3, mt: 1 }}
            placeholder="e.g., West Coast Properties"
          />

          <Tabs value={dialogTab} onChange={(e, v) => setDialogTab(v)} sx={{ mb: 2 }}>
            <Tab label="Manual Entry" />
            <Tab label="Upload CSV" />
          </Tabs>

          {dialogTab === 0 && (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Enter locations - one per line. Accepts postal codes or city/state:
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                Examples: "90210, 5000000" or "Los Angeles, CA, 5000000" or "10001"
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={8}
                value={manualInput}
                onChange={(e) => handleManualInputChange(e.target.value)}
                placeholder={`90210, 5000000\n10001, 3000000\nLos Angeles, CA, 10000000\nNew York, NY, 8000000`}
                sx={{ fontFamily: 'monospace', mb: 2 }}
              />
            </Box>
          )}

          {dialogTab === 1 && (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Upload a CSV or Excel file with postal codes or cities. Asset values can include commas (e.g., "1,500,000").
              </Typography>
              <Box sx={{ bgcolor: '#F9FAFB', p: 2, borderRadius: 1, mb: 2 }}>
                <Typography variant="caption" component="pre" sx={{ fontFamily: 'monospace' }}>
{`Format 1 - Postal Codes:
postal_code, asset_value
90210, 5000000
10001, "1,500,000"

Format 2 - Cities:
city, state, asset_value
Los Angeles, CA, 5000000
New York, NY, "$3,000,000"`}
                </Typography>
              </Box>
              <Button
                variant="outlined"
                component="label"
                startIcon={<FileUploadIcon />}
                fullWidth
              >
                Select CSV or Excel File
                <input type="file" hidden accept=".csv,.txt,.xlsx,.xls" onChange={handleFileUpload} />
              </Button>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Supported formats: .csv, .txt, .xlsx, .xls
              </Typography>
            </Box>
          )}

          {portfolioItems.length > 0 && (
            <Box sx={{ mt: 3 }}>
              <Divider sx={{ mb: 2 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  {portfolioItems.length} items loaded
                </Typography>
                <Chip 
                  icon={<AttachMoneyIcon />}
                  label={formatCurrency(totalPortfolioValue)}
                  color="success"
                />
              </Box>
              <Box sx={{ maxHeight: 200, overflow: 'auto', bgcolor: '#F9FAFB', p: 2, borderRadius: 1 }}>
                {portfolioItems.slice(0, 10).map((item, idx) => (
                  <Box key={idx} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                    <Typography variant="body2">
                      {item.postalCode 
                        ? `${item.postalCode} (${item.city || 'Unknown'}, ${item.state || 'Unknown'})`
                        : `${item.city}, ${item.state}`
                      }
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {formatCurrency(item.assetValue)}
                    </Typography>
                  </Box>
                ))}
                {portfolioItems.length > 10 && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    ... and {portfolioItems.length - 10} more
                  </Typography>
                )}
              </Box>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button 
            variant="contained" 
            onClick={createPortfolio}
            disabled={loading || !portfolioName || portfolioItems.length === 0}
            sx={{ bgcolor: '#00D4FF' }}
          >
            {loading ? <CircularProgress size={24} /> : 'Create Portfolio'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Portfolio Dialog (Read-Only) */}
      <Dialog 
        open={viewDialogOpen} 
        onClose={() => setViewDialogOpen(false)} 
        maxWidth="lg" 
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="h6">{viewEditPortfolio?.name}</Typography>
              <Typography variant="body2" color="text.secondary">
                {viewEditPortfolio?.items?.length || 0} locations • View Only
              </Typography>
            </Box>
            <IconButton onClick={() => setViewDialogOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <TableContainer component={Paper} sx={{ maxHeight: 500 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>#</TableCell>
                  <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>Postal Code</TableCell>
                  <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>City</TableCell>
                  <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>State</TableCell>
                  <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }} align="right">Asset Value</TableCell>
                  <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }} align="right">Risk Score</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {viewEditPortfolio?.items?.map((item: any, index: number) => (
                  <TableRow key={`view-${index}`} hover>
                    <TableCell>{index + 1}</TableCell>
                    <TableCell>{item.postal_code || item.zip_code}</TableCell>
                    <TableCell>{item.city || '-'}</TableCell>
                    <TableCell>{item.state || '-'}</TableCell>
                    <TableCell align="right">
                      ${(parseFloat(String(item.asset_value || item.assetValue || 0).replace(/[$,]/g, '')) || 0).toLocaleString()}
                    </TableCell>
                    <TableCell align="right">
                      <Chip 
                        label={parseFloat(String(item.risk_score || 0)).toFixed(1)} 
                        size="small"
                        color={parseFloat(String(item.risk_score || 0)) >= 7 ? 'error' : parseFloat(String(item.risk_score || 0)) >= 4 ? 'warning' : 'success'}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          
          {/* Summary */}
          <Box sx={{ mt: 2, p: 2, bgcolor: '#f9fafb', borderRadius: 1 }}>
            <Grid container spacing={2}>
              <Grid item xs={4}>
                <Typography variant="body2" color="text.secondary">Total Value</Typography>
                <Typography variant="h6" sx={{ color: '#059669' }}>
                  ${(viewEditPortfolio?.total_value || viewEditPortfolio?.totalValue || 0).toLocaleString()}
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography variant="body2" color="text.secondary">Average Risk</Typography>
                <Typography variant="h6">
                  {(viewEditPortfolio?.avg_risk || viewEditPortfolio?.avgRisk || 0).toFixed(2)}
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography variant="body2" color="text.secondary">Locations</Typography>
                <Typography variant="h6">
                  {viewEditPortfolio?.items?.length || 0}
                </Typography>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
          <Button 
            variant="contained" 
            startIcon={<EditIcon />}
            onClick={() => { 
              setViewDialogOpen(false); 
              if (viewEditPortfolio) handleEditPortfolio(viewEditPortfolio); 
            }}
          >
            Edit Portfolio
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Portfolio Dialog */}
      <Dialog 
        open={editDialogOpen} 
        onClose={() => !savingEdit && setEditDialogOpen(false)} 
        maxWidth="lg" 
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="h6">Edit Portfolio</Typography>
              <Typography variant="body2" color="text.secondary">
                Make changes and save
              </Typography>
            </Box>
            <IconButton onClick={() => !savingEdit && setEditDialogOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {/* Portfolio Name */}
          <TextField
            fullWidth
            label="Portfolio Name"
            value={editedName}
            onChange={(e) => setEditedName(e.target.value)}
            sx={{ mb: 3, mt: 1 }}
          />
          
          {/* Items Table */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="subtitle1" fontWeight={600}>
              Locations ({editedItems.length})
            </Typography>
            <Button 
              variant="outlined" 
              size="small" 
              startIcon={<AddIcon />}
              onClick={handleAddEditItem}
            >
              Add Location
            </Button>
          </Box>
          
          <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>#</TableCell>
                  <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>Postal Code</TableCell>
                  <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>City</TableCell>
                  <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>State</TableCell>
                  <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }} align="right">Asset Value ($)</TableCell>
                  <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }} align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {editedItems.map((item: any, index: number) => (
                  <TableRow key={`edit-${index}`}>
                    <TableCell>{index + 1}</TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        value={item.postal_code || ''}
                        onChange={(e) => {
                          const zip = e.target.value;
                          handleEditItemChange(index, 'postal_code', zip);
                          // Auto-fill city/state from zipcode
                          if (zip.length === 5) {
                            const lookup = zipcodes.lookup(zip);
                            if (lookup) {
                              handleEditItemChange(index, 'city', lookup.city);
                              handleEditItemChange(index, 'state', lookup.state);
                            }
                          }
                        }}
                        placeholder="12345"
                        sx={{ width: 100 }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {item.city || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {item.state || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <TextField
                        size="small"
                        type="number"
                        value={parseFloat(String(item.asset_value || 0).replace(/[$,]/g, '')) || ''}
                        onChange={(e) => handleEditItemChange(index, 'asset_value', parseFloat(e.target.value) || 0)}
                        placeholder="100000"
                        sx={{ width: 130 }}
                        InputProps={{
                          startAdornment: <Typography sx={{ mr: 0.5, color: 'text.secondary' }}>$</Typography>
                        }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <IconButton 
                        size="small" 
                        color="error"
                        onClick={() => handleRemoveEditItem(index)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          
          {/* Summary */}
          <Box sx={{ mt: 2, p: 2, bgcolor: '#f0fdf4', borderRadius: 1, border: '1px solid #bbf7d0' }}>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">Total Locations</Typography>
                <Typography variant="h6">{editedItems.length}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">Total Asset Value</Typography>
                <Typography variant="h6" sx={{ color: '#059669' }}>
                  ${editedItems.reduce((sum, item) => sum + (parseFloat(String(item.asset_value || 0).replace(/[$,]/g, '')) || 0), 0).toLocaleString()}
                </Typography>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setEditDialogOpen(false)} disabled={savingEdit}>
            Cancel
          </Button>
          <Button 
            variant="contained" 
            startIcon={savingEdit ? <CircularProgress size={16} /> : <SaveIcon />}
            onClick={handleSaveEdit}
            disabled={savingEdit || !editedName || editedItems.length === 0}
            sx={{ bgcolor: '#059669', '&:hover': { bgcolor: '#047857' } }}
          >
            {savingEdit ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}